import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function VerifyEmailConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link');
      setVerifying(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch('/api/auth/verify-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'Verification failed');
          setVerifying(false);
          return;
        }

        setSuccess(true);
        setVerifying(false);

        // Refresh user data
        mutate('/api/user');

        // Redirect to dashboard after delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } catch (err) {
        setError('An error occurred. Please try again.');
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  if (verifying) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Verifying your email...
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            Please wait while we verify your email address.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-success-surface-strong p-3">
              <CheckCircle className="h-8 w-8 text-success-foreground" />
            </div>
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Email verified
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            Your email has been verified successfully. Redirecting you to your dashboard...
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
          <Link to="/dashboard">
            <Button variant="primary" className="w-full">
              Go to dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isExpired = error.toLowerCase().includes('expired');

  return (
    <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-danger-surface-strong p-3">
            <XCircle className="h-8 w-8 text-danger-foreground" />
          </div>
        </div>
        <h2 className="text-center text-3xl font-medium text-content-heading">
          {isExpired ? 'Link expired' : 'Verification failed'}
        </h2>
        <p className="mt-4 text-center text-content-secondary">
          {error}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs space-y-4">
        <Link to="/verify-email">
          <Button variant="primary" className="w-full">
            Request new verification link
          </Button>
        </Link>
        <Link
          to="/sign-in"
          className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
